import { useEffect, useMemo, useRef, useState } from 'react';
import { useCatalogImageRotation } from '../catalog/CatalogImageRotationContext';
import { IMAGE_TRANSITION_DURATION_MS } from '../../constants/catalogImageRotation';

const MAX_PRODUCT_IMAGES = 5;
const swipeThreshold = 40;
const DEFAULT_AUTOPLAY_INTERVAL = 4500;
const loadedImageCandidates = new Set();
const inflightImagePreloads = new Map();

const toWebpCandidate = (imageUrl) => {
  if (!imageUrl) return undefined;

  if (imageUrl.includes('res.cloudinary.com')) {
    return imageUrl.replace('/upload/', '/upload/f_webp/');
  }

  return imageUrl.replace(/\.(jpg|jpeg|png)$/i, '.webp');
};

const getImageCandidates = (imageUrl) => {
  const webpCandidate = toWebpCandidate(imageUrl);
  return [...new Set([webpCandidate, imageUrl].filter(Boolean))];
};

const preloadImage = (imageUrl) =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || !imageUrl) {
      resolve(false);
      return;
    }

    const candidates = getImageCandidates(imageUrl);
    const cacheKey = candidates.join('|');

    if (candidates.some((candidate) => loadedImageCandidates.has(candidate))) {
      resolve(true);
      return;
    }

    if (inflightImagePreloads.has(cacheKey)) {
      void inflightImagePreloads.get(cacheKey).then(resolve);
      return;
    }

    const preloadPromise = new Promise((innerResolve) => {
      const tryCandidate = (candidateIndex) => {
        if (candidateIndex >= candidates.length) {
          inflightImagePreloads.delete(cacheKey);
          innerResolve(false);
          return;
        }

        const preloader = new window.Image();
        preloader.decoding = 'async';
        preloader.onload = () => {
          loadedImageCandidates.add(candidates[candidateIndex]);
          inflightImagePreloads.delete(cacheKey);
          innerResolve(true);
        };
        preloader.onerror = () => tryCandidate(candidateIndex + 1);
        preloader.src = candidates[candidateIndex];
      };

      tryCandidate(0);
    });

    inflightImagePreloads.set(cacheKey, preloadPromise);
    void preloadPromise.then(resolve);
  });

const CarouselImageLayer = ({
  imageUrl,
  alt,
  imageClassName,
  loading,
  fetchPriority,
  opacityClassName,
  animationDurationMs,
  ariaHidden = false,
}) => (
  <div
    className={`absolute inset-0 transition-opacity ease-out will-change-[opacity] ${opacityClassName}`}
    style={{ transitionDuration: `${animationDurationMs}ms` }}
    aria-hidden={ariaHidden}
  >
    <picture>
      <source srcSet={toWebpCandidate(imageUrl)} type="image/webp" />
      <img
        src={imageUrl}
        alt={ariaHidden ? '' : alt}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        className={imageClassName}
      />
    </picture>
  </div>
);

const ProductImageCarousel = ({
  images = [],
  alt,
  imageClassName = 'w-full h-full object-cover',
  containerClassName = '',
  emptyStateClassName = '',
  showDots = true,
  showArrows = true,
  loading = 'lazy',
  fetchPriority = 'auto',
  autoPlay = false,
  autoPlayInterval = DEFAULT_AUTOPLAY_INTERVAL,
}) => {
  const normalizedImages = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []).slice(0, MAX_PRODUCT_IMAGES),
    [images]
  );
  const catalogImageRotation = useCatalogImageRotation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [displayedIndex, setDisplayedIndex] = useState(0);
  const [displayedImage, setDisplayedImage] = useState(normalizedImages[0] ?? null);
  const [previousImage, setPreviousImage] = useState(null);
  const [isTransitionActive, setIsTransitionActive] = useState(false);
  const transitionSequenceRef = useRef(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [normalizedImages.join('|')]);

  useEffect(() => {
    if (normalizedImages.length === 0) {
      setDisplayedIndex(0);
      setDisplayedImage(null);
      setPreviousImage(null);
      setIsTransitionActive(false);
      return;
    }

    const nextInitialImage = normalizedImages[0];
    const currentDisplayedStillExists = displayedImage && normalizedImages.includes(displayedImage);

    if (!currentDisplayedStillExists) {
      setDisplayedIndex(0);
      setDisplayedImage(nextInitialImage);
      setPreviousImage(null);
      setIsTransitionActive(false);
    }
  }, [displayedImage, normalizedImages, normalizedImages.join('|')]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updatePreference();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  if (normalizedImages.length === 0) {
    return (
      <div
        className={`absolute inset-0 flex items-center justify-center text-sm text-light-text-muted dark:text-brand-text-muted ${containerClassName} ${emptyStateClassName}`}
      >
        No image
      </div>
    );
  }

  const hasMultipleImages = normalizedImages.length > 1;
  const usesCatalogRotation = Boolean(catalogImageRotation);
  const sharedIndex = usesCatalogRotation
    ? Math.min(catalogImageRotation.currentImageIndex, normalizedImages.length - 1)
    : null;
  const requestedIndex = sharedIndex ?? Math.min(activeIndex, normalizedImages.length - 1);
  const requestedImage = normalizedImages[requestedIndex];
  const shouldAutoPlay = autoPlay && hasMultipleImages && !usesCatalogRotation && !isPaused && !prefersReducedMotion;
  const animationDurationMs = catalogImageRotation?.transitionDurationMs ?? IMAGE_TRANSITION_DURATION_MS;
  const currentRotationIndex = usesCatalogRotation
    ? catalogImageRotation.currentImageIndex
    : activeIndex;
  const visibleIndex = Math.min(displayedIndex, normalizedImages.length - 1);

  const goToIndex = (index) => {
    if (usesCatalogRotation) {
      const nextIndex = (index + catalogImageRotation.stepCount) % catalogImageRotation.stepCount;
      catalogImageRotation.setCurrentImageIndex(nextIndex);
      return;
    }

    setActiveIndex((index + normalizedImages.length) % normalizedImages.length);
  };

  const goToPrevious = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    goToIndex(currentRotationIndex - 1);
  };

  const goToNext = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    goToIndex(currentRotationIndex + 1);
  };

  const handleTouchStart = (event) => {
    if (!usesCatalogRotation) {
      setIsPaused(true);
    }
    setTouchStartX(event.touches[0]?.clientX ?? null);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null || !hasMultipleImages) {
      if (!usesCatalogRotation) {
        setIsPaused(false);
      }
      setTouchStartX(null);
      return;
    }

    const endX = event.changedTouches[0]?.clientX ?? touchStartX;
    const delta = endX - touchStartX;
    if (!usesCatalogRotation) {
      setIsPaused(false);
    }
    setTouchStartX(null);

    if (Math.abs(delta) < swipeThreshold) return;

    if (delta > 0) {
      goToPrevious(event);
      return;
    }

    goToNext(event);
  };

  useEffect(() => {
    if (!shouldAutoPlay) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % normalizedImages.length);
    }, autoPlayInterval);

    return () => window.clearInterval(intervalId);
  }, [autoPlayInterval, normalizedImages.length, shouldAutoPlay]);

  useEffect(() => {
    if (!hasMultipleImages || typeof window === 'undefined') {
      return undefined;
    }

    const nextIndex = usesCatalogRotation
      ? Math.min((catalogImageRotation.currentImageIndex + 1) % catalogImageRotation.stepCount, normalizedImages.length - 1)
      : (Math.min(activeIndex, normalizedImages.length - 1) + 1) % normalizedImages.length;

    const nextImage = normalizedImages[nextIndex];
    if (!nextImage || nextImage === displayedImage) {
      return undefined;
    }

    void preloadImage(nextImage);
    return undefined;
  }, [
    activeIndex,
    catalogImageRotation,
    displayedImage,
    hasMultipleImages,
    normalizedImages,
    usesCatalogRotation,
  ]);

  useEffect(() => {
    if (!requestedImage || requestedImage === displayedImage) {
      if (displayedIndex !== requestedIndex) {
        setDisplayedIndex(requestedIndex);
      }
      return undefined;
    }

    let isCancelled = false;
    let animationFrameId = null;
    let clearPreviousTimerId = null;
    const transitionId = transitionSequenceRef.current + 1;
    transitionSequenceRef.current = transitionId;

    const beginTransition = async () => {
      const imageReady = await preloadImage(requestedImage);

      if (isCancelled || transitionSequenceRef.current !== transitionId || !imageReady) {
        return;
      }

      const outgoingImage = displayedImage;

      setPreviousImage(outgoingImage);
      setDisplayedImage(requestedImage);
      setDisplayedIndex(requestedIndex);
      setIsTransitionActive(false);

      animationFrameId = window.requestAnimationFrame(() => {
        if (isCancelled || transitionSequenceRef.current !== transitionId) {
          return;
        }

        setIsTransitionActive(true);
      });

      clearPreviousTimerId = window.setTimeout(() => {
        if (isCancelled || transitionSequenceRef.current !== transitionId) {
          return;
        }

        setPreviousImage(null);
        setIsTransitionActive(false);
      }, animationDurationMs);
    };

    void beginTransition();

    return () => {
      isCancelled = true;
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (clearPreviousTimerId !== null) {
        window.clearTimeout(clearPreviousTimerId);
      }
    };
  }, [animationDurationMs, displayedImage, displayedIndex, requestedImage, requestedIndex]);

  return (
    <div
      className={`absolute inset-0 ${containerClassName}`}
      onMouseEnter={() => {
        if (!usesCatalogRotation) {
          setIsPaused(true);
        }
      }}
      onMouseLeave={() => {
        if (!usesCatalogRotation) {
          setIsPaused(false);
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {previousImage ? (
        <CarouselImageLayer
          imageUrl={previousImage}
          alt={alt}
          imageClassName={imageClassName}
          loading={loading}
          fetchPriority={fetchPriority}
          opacityClassName={isTransitionActive ? 'opacity-0' : 'opacity-100'}
          animationDurationMs={animationDurationMs}
          ariaHidden
        />
      ) : null}

      {displayedImage ? (
        <CarouselImageLayer
          imageUrl={displayedImage}
          alt={alt}
          imageClassName={imageClassName}
          loading={loading}
          fetchPriority={fetchPriority}
          opacityClassName={previousImage ? (isTransitionActive ? 'opacity-100' : 'opacity-0') : 'opacity-100'}
          animationDurationMs={animationDurationMs}
        />
      ) : null}

      {hasMultipleImages && showArrows && (
        <>
          <button
            type="button"
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-2 text-white transition hover:bg-black/70"
            aria-label="Previous image"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-2 text-white transition hover:bg-black/70"
            aria-label="Next image"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      )}

      {hasMultipleImages && showDots && (
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2 rounded-full bg-black/35 px-3 py-1.5">
          {normalizedImages.map((imageUrl, index) => (
            <button
              key={`${imageUrl}-${index}`}
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goToIndex(index);
              }}
              className={`h-2.5 w-2.5 rounded-full transition ${
                index === visibleIndex ? 'bg-white' : 'bg-white/45 hover:bg-white/70'
              }`}
              aria-label={`View image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImageCarousel;
